import { BodyScrollEvent, GridApi, GridReadyEvent } from 'ag-grid-community';
import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useRef } from 'react';
import { useWindowResizeListener } from '../../hooks/common';
import Table, { TableProps } from '../AGTable/AGTable';
import FooterClone from './FooterClone';

import styles from './FullPage.module.scss';

const cx = classNames.bind(styles);

export interface FullPageTableProps<TData> extends TableProps<TData> {
  pageTitle: string;
  paginationComponent?: React.ReactElement;
  headerComponent?: React.ReactElement;
}

// TODO calculate these at runtime
const PAGE_HEADER_HEIGHT = 104;
const PAGE_FOOTER_HEIGHT = 204; // footer + pagination spacer
const TABLE_HEADER_HEIGHT = 80;

const FullPageTable = <TData,>({
  pageTitle,
  onGridReady,
  onBodyScroll,
  rowData,
  paginationComponent,
  headerComponent,
  ...props
}: FullPageTableProps<TData>): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef<number>(0);
  const previousDistanceToLastRowRef = useRef<number>(0);
  const gridApiRef = useRef<GridApi | null>(null);

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api;
      onGridReady?.(event);
    },
    [onGridReady]
  );

  useWindowResizeListener(() => gridApiRef.current?.sizeColumnsToFit());

  const adjustScrollContainerHeight = (offset: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.height = `calc(100vh - ${PAGE_HEADER_HEIGHT + offset}px)`;
    }
  };

  const adjustContainerTopPosition = (offset: number) => {
    if (containerRef.current) {
      containerRef.current.style.top = `${PAGE_HEADER_HEIGHT - Math.min(offset, TABLE_HEADER_HEIGHT)}px`;
    }
  };

  const handleBodyScroll = useCallback(
    (event: BodyScrollEvent) => {
      if (event.direction !== 'vertical' || !scrollContainerRef.current || !containerRef.current || !rowData) {
        onBodyScroll?.(event);
        return;
      }

      const TABLE_ROWS_CONTAINER_HEIGHT = (rowData?.length ?? 0) * event.api.getSizesForCurrentTheme().rowHeight;
      // adjusting for offset that was applied to height
      const viewportTopPosition = event.api.getVerticalPixelRange().top;
      containerRef.current.style.top = `${PAGE_HEADER_HEIGHT - Math.min(viewportTopPosition, TABLE_HEADER_HEIGHT)}px`;
      containerRef.current.style.height = `calc(100vh - ${PAGE_HEADER_HEIGHT - viewportTopPosition}px)`;

      const viewportBottomPosition = event.api.getVerticalPixelRange().bottom + offsetRef.current;
      const distanceToLastRow = TABLE_ROWS_CONTAINER_HEIGHT - viewportBottomPosition;

      let pxToBottom = 0;
      const isScrollingUp = previousDistanceToLastRowRef.current > viewportTopPosition;
      if (isScrollingUp) {
        pxToBottom = PAGE_FOOTER_HEIGHT + distanceToLastRow;
      } else {
        pxToBottom = Math.max(0, Math.min(PAGE_FOOTER_HEIGHT, distanceToLastRow));
      }
      previousDistanceToLastRowRef.current = viewportTopPosition;
      offsetRef.current = Math.max(0, PAGE_FOOTER_HEIGHT - pxToBottom);
      adjustScrollContainerHeight(offsetRef.current);
      onBodyScroll?.(event);
    },
    [rowData]
  );

  useEffect(() => {
    if (gridApiRef.current?.getDisplayedRowAtIndex(0)) {
      gridApiRef.current?.ensureIndexVisible(0, 'top');
    }
    adjustContainerTopPosition(0);
    adjustScrollContainerHeight(0);
  }, [rowData]);

  return (
    <div className={cx('body')}>
      <div className={cx('full-page-container', 'full-page-container--hide')} ref={containerRef}>
        <div className={cx('header')}>
          <h1>{pageTitle}</h1>
          <div className={cx('sub-header')}>{headerComponent}</div>
        </div>
        <div className={cx('content')}>
          <div ref={scrollContainerRef} style={{ height: `calc(100vh - ${PAGE_HEADER_HEIGHT}px)` }}>
            <Table
              onBodyScroll={handleBodyScroll}
              onGridReady={handleGridReady}
              rowData={rowData}
              {...props}
              suppressRowVirtualisation
            />
          </div>
          <div className={cx('pagination')}>{paginationComponent}</div>
        </div>
        <FooterClone />
      </div>
    </div>
  );
};

export default FullPageTable;
